import { FormDialog } from '@/components/FormDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { GatewayType } from '@/constants/gateway'
import { useGetDepartments } from '@/features/departments/hooks/useDepartment'
import { useUI } from '@/hooks/useUI'
import { getMutationOptions } from '@/utils/mutation'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateGateway, useGetGateway, useUpdateGateway } from '../hooks/useGateway'

export function GatewayDialog({
  open,
  onOpenChange,
  gatewayId,
  floorId,
  point,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  gatewayId?: string | null
  floorId: string
  point?: [number, number] | null
}) {
  const { t } = useTranslation()
  const { gatewayTypes } = useUI()
  const { data: gateway, isLoading: isLoadingGateway } = useGetGateway(gatewayId ?? '', open)
  const { data: departments, isLoading: isLoadingDepartments } = useGetDepartments({ floorId }, open)

  const createGateway = useCreateGateway()
  const updateGateway = useUpdateGateway()

  const [form, setForm] = useState({
    name: '',
    type: '' as GatewayType,
    departmentId: '',
    externalId: '',
    referenceRssi: '',
  })

  const isEdit = !!gatewayId
  const isInvalid = !form.name || !form.type || !floorId || (!point && !gateway?.coordinates)
  const isLoading = isLoadingGateway || createGateway.isPending || updateGateway.isPending

  useEffect(() => {
    if (gateway) {
      setForm({
        name: gateway.name,
        type: gateway.type,
        departmentId: gateway?.departmentId ?? '',
        externalId: gateway?.externalId ?? '',
        referenceRssi: gateway?.referenceRssi?.toString() ?? '',
      })
    } else {
      setForm({ name: '', type: 'BLE', departmentId: '', externalId: '', referenceRssi: '' })
    }
  }, [gateway, open])

  function handleSubmit() {
    if (isInvalid) return
    const options = getMutationOptions({
      successMessage: isEdit ? t('gateway.toast.editSuccess') : t('gateway.toast.addSuccess'),
      onSuccess: () => {
        onOpenChange(false)
      },
    })

    const payload = {
      ...form,
      coordinates: point ? JSON.stringify(point) : gateway?.coordinates,
      floorId: floorId ?? gateway?.floorId,
      departmentId: form.departmentId === 'none' ? null : form.departmentId,
      referenceRssi: form.referenceRssi ? parseInt(form.referenceRssi) : null,
    }

    if (isEdit && gateway) {
      updateGateway.mutate({ id: gateway.id, payload }, options)
    } else {
      createGateway.mutate({ payload }, options)
    }
  }
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('gateway.edit') : t('gateway.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      contentTestId="gateway-dialog"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('gateway.name')} *</Label>
          <Input
            data-testid="gateway-form-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="BLE"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('gateway.type')} *</Label>
          <Select value={form.type} onValueChange={v => setForm({ ...form, type: v as GatewayType })}>
            <SelectTrigger data-testid="gateway-form-type-trigger" className="h-9 bg-secondary/50 w-full">
              <SelectValue placeholder={t('common.placeholder.selectType')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {gatewayTypes?.map(g => (
                <SelectItem data-testid={`gateway-form-type-option-${g.value}`} key={g.value} value={g.value}>
                  {g.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.department')} *</Label>
          {/* <div className="relative flex gap-2"> */}
          <Select value={form.departmentId} onValueChange={v => setForm({ ...form, departmentId: v })}>
            <SelectTrigger data-testid="gateway-form-department-trigger" className="h-9 bg-secondary/50 w-full">
              <SelectValue placeholder={t('common.placeholder.selectDepartment')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              <SelectItem data-testid="gateway-form-department-option-none" value="none">- {t('common.none')} -</SelectItem>
              {departments?.map(d => (
                <SelectItem data-testid={`gateway-form-department-option-${d.id}`} key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('gateway.referenceId')}</Label>
          <Input
            data-testid="gateway-form-external-id"
            value={form.externalId}
            onChange={e => setForm({ ...form, externalId: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('gateway.referenceDistance')}</Label>
          <Input
            data-testid="gateway-form-reference-rssi"
            value={form.referenceRssi}
            onChange={e => setForm({ ...form, referenceRssi: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
      </div>
    </FormDialog>
  )
}
